/**
 * Payment/ID redaction for anything the customer types.
 *
 * The backend already does this on arrival (app/aero/safety.py,
 * ``redact_sensitive``) so nothing sensitive is ever persisted. That check
 * stays where it is — it defends the API against any client. But it runs
 * after the message has been sent and after the bubble has been drawn, so a
 * pasted card number still appeared in the customer's own message on screen,
 * for as long as the conversation stayed open. QA raised exactly that.
 *
 * Redacting here means the number never reaches the bubble and never leaves
 * the browser. The patterns are deliberately the same ones, in the same
 * order; if you change one side, change the other.
 */

const REDACTIONS: RegExp[] = [
  // Card: 13 digits or more, in the usual 4-4-4-N grouping.
  /\b\d{4}[ -]?\d{4}[ -]?\d{4}[ -]?\d{1,4}\b/g,
  /\bcvv\s*[:\-]?\s*\d{3,4}\b/gi,
  // Aadhaar: spaced triples, or a bare twelve.
  /\b\d{4}\s\d{4}\s\d{4}\b|\b\d{12}\b/g,
  /\b[A-Z]{5}\d{4}[A-Z]\b/g,
  /\b(?:\+?91[-\s]?)?[6-9]\d{9}\b/g,
];

/**
 * ``text`` with anything payment- or ID-shaped replaced by ``[REDACTED]``.
 *
 * A budget is safe: the shortest pattern here needs ten digits, so "250000"
 * and "2,50,000" pass through untouched.
 */
export function redactSensitive(text: string): string {
  let out = text;
  for (const pattern of REDACTIONS) {
    out = out.replace(pattern, "[REDACTED]");
  }
  return out;
}
